/**
 * Parser for Web Share Target data (links from YouTube, TikTok, Facebook, etc., and files)
 */

const URL_REGEX = /(https?:\/\/[^\s]+)/gi;

// Clean common tracking parameters from URLs
export const cleanUrl = (rawUrl) => {
    try {
        const parsed = new URL(rawUrl);
        const trackingParams = [
            'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
            'fbclid', 'si', '_r', '_t', 'checksum', 'sfnsn', 'igshid'
        ];
        trackingParams.forEach(param => parsed.searchParams.delete(param));
        return parsed.toString();
    } catch {
        return rawUrl;
    }
};

// Identify platform from URL
export const detectPlatform = (urlStr) => {
    try {
        const host = new URL(urlStr).hostname.toLowerCase();
        if (host.includes('youtube.com') || host.includes('youtu.be')) return 'youtube';
        if (host.includes('tiktok.com')) return 'tiktok';
        if (host.includes('facebook.com') || host.includes('fb.watch') || host.includes('fb.com')) return 'facebook';
        if (host.includes('instagram.com')) return 'instagram';
        if (host.includes('twitter.com') || host.includes('x.com')) return 'twitter';
        if (host.includes('linkedin.com')) return 'linkedin';
        return 'link';
    } catch {
        return null;
    }
};

/**
 * Parses shared payload into note form fields
 */
export const parseSharedPayload = (payload) => {
    const rawTitle = (payload?.title || '').trim();
    const rawText = (payload?.text || '').trim();
    const rawUrl = (payload?.url || '').trim();
    const rawFiles = payload?.files || [];

    const tags = [];
    const extractedUrls = [];

    // 1. Extract URLs from rawUrl and rawText
    if (rawUrl) {
        const cleaned = cleanUrl(rawUrl);
        if (!extractedUrls.includes(cleaned)) extractedUrls.push(cleaned);
    }

    if (rawText) {
        const matches = rawText.match(URL_REGEX);
        if (matches) {
            matches.forEach(m => {
                const cleaned = cleanUrl(m);
                if (!extractedUrls.includes(cleaned)) extractedUrls.push(cleaned);
            });
        }
    }

    // 2. Platform detection and auto-tagging
    extractedUrls.forEach(u => {
        const platform = detectPlatform(u);
        if (platform && !tags.includes(platform)) {
            tags.push(platform);
        }
    });

    // 3. Format Title
    let title = rawTitle;
    let textWithoutUrl = rawText;
    if (extractedUrls.length > 0) {
        // Strip URLs from text to see if there is descriptive text/title
        textWithoutUrl = rawText.replace(URL_REGEX, '').trim();
    }

    if (!title) {
        if (textWithoutUrl) {
            // First line of text without URL as title
            const firstLine = textWithoutUrl.split('\n')[0].trim();
            title = firstLine.length > 80 ? firstLine.substring(0, 77) + '...' : firstLine;
        } else if (extractedUrls.length > 0) {
            const platform = detectPlatform(extractedUrls[0]);
            if (platform === 'youtube') title = 'Wideo z YouTube';
            else if (platform === 'tiktok') title = 'Wideo z TikToka';
            else if (platform === 'facebook') title = 'Post z Facebooka';
            else title = 'Link: ' + extractedUrls[0];
        } else if (rawFiles.length > 0) {
            title = rawFiles.length === 1 
                ? (rawFiles[0].name || 'Załącznik')
                : `Załączniki (${rawFiles.length})`;
        }
    }

    if (title) {
        title = title.replace(/[:\-–—\s]+$/, '').trim();
    }

    // 4. Format Content
    let content = '';
    if (textWithoutUrl && extractedUrls.length > 0) {
        content = `${textWithoutUrl}\n\n${extractedUrls.join('\n')}`;
    } else if (extractedUrls.length > 0) {
        content = extractedUrls.join('\n');
    } else if (rawText) {
        content = rawText;
    }

    // 5. Convert stored files to File objects
    const files = [];
    if (rawFiles && Array.isArray(rawFiles)) {
        for (const item of rawFiles) {
            try {
                if (item instanceof File) {
                    files.push(item);
                } else if (item.blob) {
                    const reconstructedFile = new File([item.blob], item.name || 'file', {
                        type: item.type || 'application/octet-stream',
                        lastModified: item.lastModified || Date.now()
                    });
                    files.push(reconstructedFile);
                }
            } catch (err) {
                console.warn('[ShareParser] File reconstruction error:', err);
            }
        }
    }

    if (files.length > 0 && !tags.includes('pliki')) {
        tags.push('pliki');
    }

    return {
        title,
        content,
        tags,
        files,
        hasSharedData: Boolean(title || content || files.length > 0)
    };
};
